import axios from "axios";
import moment from "moment-timezone";
import { FC, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastOptions } from "react-toastify";
import * as XLSX from "xlsx";
import Config from "@hero/shared/models/config";
import { IUserInfo } from "@hero/shared/models/userInfo";
import {
    IUploadWorkingTimeRequest,
    IUploadWorkingTimeResult,
    IUploadWorkingTimeRow,
} from "@hero/shared/models/workingTimeUploadDay";
import { getRowsDayRange, partitionRowsByRange, toDayKey } from "@hero/shared/utils/working-time-upload";
import { getAllWorkingTimeInfo } from "../../../../redux/actions/workingTime.action";
import { getWorkingTimeUploadInfo } from "../../../../redux/actions/workingTimeUpload.action";
import { timezoneSetHours } from "../../../../util";
import Url from "../../../../util/url";

const A_CODE = 97;
const TOAST_OPTIONS: ToastOptions = {
    position: "top-center",
    autoClose: 2000,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
};
const formatDayKey = (key: string) => moment(key, "YYYY-MM-DD").format("DD/MM");

const UpdateTimeAttendanceModal: FC<{
    setUpdateWorkingTimeModalVisible: any;
    startTime?: string;
    endTime?: string;
}> = ({ setUpdateWorkingTimeModalVisible, startTime, endTime }) => {
    const dispatch = useDispatch();
    const allUsers: IUserInfo[] = useSelector((state: any) => state.userInfoState.userInfos);
    const currentUser: IUserInfo = useSelector((state: any) => state.authState.user);
    const todayKey = toDayKey(new Date());
    const [rows, setRows] = useState<IUploadWorkingTimeRow[]>([]);
    const [fileName, setFileName] = useState("");
    const [unknownEmployees, setUnknownEmployees] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<IUploadWorkingTimeResult | null>(null);

    // Khoảng ngày tự nhận từ file: [ngày nhỏ nhất, ngày lớn nhất]
    const fileRange = useMemo(() => getRowsDayRange(rows), [rows]);
    const rangeError = fileRange && fileRange.start > todayKey ? "File chỉ có dữ liệu của ngày tương lai" : "";
    const preview = useMemo(
        () => (fileRange && !rangeError ? partitionRowsByRange(rows, fileRange.start, fileRange.end, todayKey) : null),
        [rows, fileRange, todayKey, rangeError]
    );
    const ignoredDays = useMemo(
        () => (preview ? Array.from(new Set(preview.ignored.map((r) => toDayKey(r.date)))).sort() : []),
        [preview]
    );
    const canUpload = !!preview && unknownEmployees.length === 0 && !uploading;

    const close = () => setUpdateWorkingTimeModalVisible(false);

    const refetch = () => {
        if (startTime && endTime) {
            dispatch(
                getAllWorkingTimeInfo({
                    start: timezoneSetHours(startTime).getTime(),
                    end: timezoneSetHours(endTime, 23, 59, 59).getTime(),
                })
            );
            dispatch(getWorkingTimeUploadInfo({ startDate: startTime, endDate: endTime }));
        }
    };

    const uploadNewInfo = async () => {
        if (!canUpload || !fileRange) return;
        const body: IUploadWorkingTimeRequest = {
            startDate: fileRange.start,
            endDate: fileRange.end,
            fileName,
            uploadedBy: currentUser?._id,
            rows,
        };
        setUploading(true);
        try {
            const response = await axios.post<IUploadWorkingTimeResult>(Url("working-time-upload/upload"), body);
            setResult(response.data);
            if (response.data.inserted + response.data.updated > 0) {
                toast.success("Cập nhật dữ liệu chấm công thành công", TOAST_OPTIONS);
            } else {
                toast.info("Không có thay đổi dữ liệu chấm công", TOAST_OPTIONS);
            }
            refetch();
        } catch (error: any) {
            console.log("upload error", error);
            toast.error("Upload thất bại: " + (error?.response?.data ?? error?.message ?? ""), TOAST_OPTIONS);
        } finally {
            setUploading(false);
        }
    };

    const loadFileXLSX = async (e: any) => {
        const selected: File | undefined = e.target.files?.[0];
        e.target.value = "";
        setResult(null);
        if (!selected) return;
        setFileName(selected.name);
        const workbook = XLSX.read(await selected.arrayBuffer());
        const Sheet = workbook.Sheets[workbook.SheetNames[0]];
        let data: any = [];

        const getCellLocation = (key: string) => {
            //column only in alphabet !!!!!!!!!!! (1 character)
            if (key.includes("!")) return [-1, -1];
            key = key.toLowerCase();
            const col = key.charCodeAt(0) - A_CODE;
            const row = parseInt(key.substring(1, key.length));
            if (row >= 0 && col >= 0) return [row, col];
            else return [-1, -1];
        };

        const formatTime = (time: string) => {
            let [h, m] = time.split(":");
            if (h.length == 1) h = "0" + h;
            if (m.length == 1) m = "0" + m;
            return h + ":" + m;
        };
        for (let cell in Sheet) {
            let [row, col] = getCellLocation(cell);
            if (row >= 0 && col >= 0) {
                if (!!!data[row]) data[row] = [];
                data[row][col] = Sheet[cell].w.trim();
            }
        }
        const undefineEmployee: string[] = [];
        const parsed: IUploadWorkingTimeRow[] = data
            .filter((item: any) => item.length > 1)
            .map((item: any) => {
                try {
                    const date = moment.tz(item[3], "M/D/YY", Config.TIMEZONE).toDate();
                    const user = allUsers.find(
                        (u) =>
                            u.employeeCode.toLowerCase() === item[1].trim().toLowerCase() &&
                            u.status == Config.STATUS_PUBLIC
                    );
                    if (
                        !user &&
                        date.toString() !== "Invalid Date" &&
                        !undefineEmployee.includes(item[1].trim() + " " + item[2].trim())
                    )
                        undefineEmployee.push(item[1].trim() + " " + item[2].trim());
                    const row: IUploadWorkingTimeRow = {
                        userId: user?._id ?? "",
                        date,
                        checkin: item[6] ? formatTime(item[6]) : "",
                        checkout: item[7] ? formatTime(item[7]) : "",
                    };
                    return row;
                } catch (e) {
                    return null;
                }
            })
            .filter((item: IUploadWorkingTimeRow | null) => !!item && item.date.toString() !== "Invalid Date");
        setRows(parsed);
        setUnknownEmployees(undefineEmployee);
    };

    return (
        <Modal show={true} onHide={close} size="lg" className="update-working-time-modal">
            <Modal.Header closeButton>
                <Modal.Title>Cập nhật máy chấm công</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-2">
                    <strong>1. Chọn file</strong>
                </div>
                <input
                    type="file"
                    accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={loadFileXLSX}
                />
                {unknownEmployees.length > 0 && (
                    <div className="text-danger mt-2">
                        Không tìm thấy mã nhân viên của: {unknownEmployees.join(", ")}. Hãy cập nhật để tiếp tục!
                    </div>
                )}

                {rangeError && <div className="text-danger mt-2">{rangeError}</div>}

                {fileRange && preview && (
                    <div className="mt-3">
                        <strong>2. Kiểm tra trước khi cập nhật</strong>
                        <ul className="mt-2 mb-0">
                            <li>
                                Khoảng ngày trong file: {formatDayKey(fileRange.start)} – {formatDayKey(fileRange.end)}
                                {fileRange.end > todayKey && ` (chỉ xử lý đến ${formatDayKey(todayKey)})`}
                            </li>
                            <li>Dòng hợp lệ: {preview.inRange.length}</li>
                            <li>
                                Dòng ngày tương lai (bỏ qua): {preview.ignored.length}
                                {ignoredDays.length > 0 && ` (${ignoredDays.map(formatDayKey).join(", ")})`}
                            </li>
                            <li>Ngày có dữ liệu: {preview.loadedDays.length}</li>
                            <li>
                                Ngày không có dòng nào (đánh dấu "trống"):{" "}
                                {preview.emptyDays.length ? preview.emptyDays.map(formatDayKey).join(", ") : "không có"}
                            </li>
                        </ul>
                    </div>
                )}

                {result && (
                    <div className="alert alert-success mt-3 mb-0">
                        Thêm mới {result.inserted}, cập nhật {result.updated}, bỏ qua {result.ignored} dòng. Đã đánh
                        dấu {result.loadedDays.length} ngày có dữ liệu, {result.emptyDays.length} ngày trống.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={close}>
                    Đóng
                </Button>
                <Button variant="primary" disabled={!canUpload} onClick={uploadNewInfo}>
                    {uploading ? "Đang cập nhật..." : "Cập nhật"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateTimeAttendanceModal;
